import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { QueryKey, QueryOptions, useQuery } from '@tanstack/react-query';
import { BaseResponse } from 'models/_base';
import { isDeepEqual } from 'utils/objectUtils';
import { getTotalCount } from '../utils/pagination';
import useTable from './useTable';

export interface ExpectedQueryData<T> extends BaseResponse {
  data: T[];
}

interface UseServerFilteredTableArgs<
  TData,
  TQueryData extends ExpectedQueryData<TData> = ExpectedQueryData<TData>,
> {
  /**
   * @description Array of column definitions for the table, following the structure expected by TanStack Table. Each column definition can include properties such as `id`, `header`, `accessorKey`, `cell`, and others to define how the column should be rendered and behave. The columns are used by the table instance to render the appropriate headers and cells for each row of data.
   */
  columns: ColumnDef<TData, any>[];
  /**
   * @description Function that returns the query options for fetching data based on the current pagination, sorting, and filters. This function is called whenever the page index, page size, sorting state, or submitted filters change. The returned query options are then used by React Query to fetch the appropriate data for the table. The function receives an object with the current pageIndex, pageSize, and sorting state, and should return an object containing at least a `queryKey` and a `queryFn` for React Query to execute.
   */
  getQuery: (arg: {
    pageIndex: number;
    pageSize: number;
    sorting: SortingState;
  }) => QueryOptions<TQueryData>;
  /**
   * @description Initial page size for the table pagination. This is optional and defaults to 20 if not provided. The page size can be changed later by the user through the pagination controls, but this sets the initial number of items per page when the table first loads.
   */
  pageSize?: number;
  /**
   * @description Optional filter function to apply to the data returned from the query before it's passed to the table. This is useful for cases where certain filters are easier to apply client-side after fetching a broader dataset from the server. Note that using this will disable accurate page count and pagination controls, since the filtering is done client-side on the already fetched data.
   */
  filter?: (data: TData[]) => TData[];
}

const useServerFilteredTable = <
  TData,
  TQueryData extends ExpectedQueryData<TData> = ExpectedQueryData<TData>,
>({
  columns,
  getQuery,
  pageSize: pageSizeParam = 20,
  filter,
}: UseServerFilteredTableArgs<TData, TQueryData>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPage] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizeParam,
  });

  // Derive a "filter key" by calling getQuery with pageIndex=0. This produces a
  // queryKey that reflects filters and sorting but is insensitive to the current
  // page, so it can be compared across renders to detect filter/sort changes.
  const filterBaseOptions = getQuery({ pageIndex: 0, pageSize, sorting });

  // trackedQueryKey mirrors filterBaseOptions.queryKey in state so it always
  // updates atomically with pageIndex (batched together) to prevent stale pageIndex.
  const [trackedQueryKey, setTrackedQueryKey] = useState<QueryKey | undefined>(
    filterBaseOptions.queryKey,
  );

  const hasChangedFilters = useMemo(
    () => !isDeepEqual(filterBaseOptions.queryKey, trackedQueryKey),
    [filterBaseOptions.queryKey, trackedQueryKey],
  );

  // When filters have changed, re-use filterBaseOptions (pageIndex=0) so the
  // server immediately receives the correct page. When unchanged, fetch the
  // actual current page.
  const queryOptions = hasChangedFilters
    ? filterBaseOptions
    : getQuery({ pageIndex, pageSize, sorting });

  const { data, isLoading, isFetching } = useQuery({
    keepPreviousData: true,
    ...queryOptions,
  });

  const queryData = data?.data ?? [];
  const filteredData = filter ? filter(queryData) : queryData;

  const totalCount = getTotalCount(data?._metadata);

  const tableInstance = useTable({
    data: filteredData,
    columns,
    manualPagination: true,
    manualSorting: true,
    pageCount: totalCount ? Math.ceil(totalCount / pageSize) : -1,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    onPaginationChange: setPage,
    onSortingChange: setSorting,
  });

  const isEmpty = !data?.data?.length;

  // Once the fetch triggered by a filter/sort change has settled, atomically
  // reset pageIndex to 0 and sync trackedQueryKey. Waiting for isFetching to be
  // false means the visual page index only updates after new data has arrived.
  useEffect(() => {
    if (!isFetching && hasChangedFilters) {
      setPage((prev) => ({ ...prev, pageIndex: 0 }));
      setTrackedQueryKey(filterBaseOptions.queryKey);
    }
  }, [isFetching, hasChangedFilters, filterBaseOptions.queryKey]);

  return { tableInstance, isLoading, isEmpty, columns, data };
};

export default useServerFilteredTable;
